import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { 
  Castle, 
  PartyPopper, 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Calendar as CalendarIcon,
  Clock,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Zap,
  Check,
  CheckCircle,
  PartyPopperIcon,
  AlertCircle,
  X,
  ZoomIn
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to get full image URL
const getImageUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

// Default time slots (fallback if no config)
const DEFAULT_TIME_SLOTS = [
  { id: "matin", name: "Matin", hours: "8h - 13h" },
  { id: "apres-midi", name: "Après-midi", hours: "14h - 19h" },
  { id: "journee", name: "Journée complète", hours: "8h - 19h" },
];

export default function PublicBookingPage() {
  const { slug } = useParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Image lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);

  // Organization & catalogue data
  const [organization, setOrganization] = useState(null);
  const [structures, setStructures] = useState([]);
  const [offers, setOffers] = useState([]);
  const [options, setOptions] = useState([]);
  const [timeSlots, setTimeSlots] = useState(DEFAULT_TIME_SLOTS);

  // Availability data
  const [structuresAvailability, setStructuresAvailability] = useState({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  const [formData, setFormData] = useState({
    date: null,
    time_slot: "",
    structure_id: "",
    offer_id: "",
    client_name: "",
    client_phone: "",
    client_email: "",
    client_address: "",
    options: [],
    needs_generator: false,
    notes: "",
  });

  useEffect(() => {
    fetchBookingData();
  }, [slug]);

  useEffect(() => {
    if (formData.date && formData.time_slot && structures.length > 0) {
      fetchAllStructuresAvailability();
    }
  }, [formData.date, formData.time_slot, structures]);

  const fetchBookingData = async () => {
    try {
      const response = await fetch(`${API}/booking/${slug}`);
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Page non trouvée");
      }
      const data = await response.json();
      setOrganization(data.organization);
      setStructures(data.structures);
      setOffers(data.offers);
      setOptions(data.options);
      
      // Use time slots from API response if available
      if (data.time_slots && data.time_slots.length > 0) {
        setTimeSlots(data.time_slots);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStructuresAvailability = async () => {
    if (!formData.date) return;

    setLoadingAvailability(true);
    const dateStr = format(formData.date, "yyyy-MM-dd");
    const availability = {};

    try {
      const promises = structures.map(async (structure) => {
        const response = await fetch(
          `${API}/booking/${slug}/availability/${structure.id}?date=${dateStr}`
        );
        if (response.ok) {
          const data = await response.json();
          return { id: structure.id, data };
        }
        return { id: structure.id, data: { booked_slots: [] } };
      });

      const results = await Promise.all(promises);
      results.forEach(({ id, data }) => {
        availability[id] = data;
      });

      setStructuresAvailability(availability);
    } catch (err) {
    } finally {
      setLoadingAvailability(false);
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === "date" || field === "time_slot") {
      setFormData((prev) => ({ ...prev, [field]: value, structure_id: "" }));
    }
  };

  const toggleOption = (optionId) => {
    setFormData((prev) => ({
      ...prev,
      options: prev.options.includes(optionId)
        ? prev.options.filter((id) => id !== optionId)
        : [...prev.options, optionId],
    }));
  };

  const isStructureAvailable = (structureId) => {
    const availability = structuresAvailability[structureId];
    if (!availability) return true;

    const bookedSlots = availability.booked_slots || [];
    const selectedSlot = formData.time_slot;

    if (selectedSlot === "journee") {
      return !bookedSlots.includes("matin") && 
             !bookedSlots.includes("apres-midi") && 
             !bookedSlots.includes("journee");
    } else {
      return !bookedSlots.includes(selectedSlot) && !bookedSlots.includes("journee");
    }
  };

  // Get structure price based on time slot
  const getStructurePrice = (structure) => {
    if (!structure) return 0;
    if (formData.time_slot === "journee") {
      return structure.price_full_day || 0;
    }
    return structure.price_half_day || 0;
  };

  // Get offers compatible with selected structure
  const getCompatibleOffers = () => {
    if (!formData.structure_id) return [];
    return offers.filter(offer => {
      if (!offer.included_structures || offer.included_structures.length === 0) return false;
      return offer.included_structures.some(inc => inc.structure_id === formData.structure_id);
    });
  };

  // Get supplement for structure in selected offer
  const getStructureSupplement = () => {
    if (!formData.offer_id || !formData.structure_id) return 0;
    const offer = offers.find(o => o.id === formData.offer_id);
    if (!offer || !offer.included_structures) return 0;
    const included = offer.included_structures.find(inc => inc.structure_id === formData.structure_id);
    return included?.supplement || 0;
  };

  const calculateTotal = () => {
    let total = 0;
    const structure = structures.find(s => s.id === formData.structure_id);
    const offer = offers.find(o => o.id === formData.offer_id);
    
    if (offer) {
      // With offer: offer price + structure supplement (if any)
      total += offer.price;
      total += getStructureSupplement();
    } else if (structure) {
      // No offer: structure price based on time slot
      total += getStructurePrice(structure);
    }

    // Add options
    formData.options.forEach((optId) => {
      const opt = options.find((o) => o.id === optId);
      if (opt) total += opt.price;
    });

    if (formData.needs_generator) {
      total += 50;
    }

    return total;
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      const response = await fetch(`${API}/booking/${slug}/reserve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          date: format(formData.date, "yyyy-MM-dd"),
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Erreur lors de la réservation");
      }

      const result = await response.json();
      setSuccess(result);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return formData.date && formData.time_slot;
      case 2:
        return formData.structure_id;
      case 3:
        // Offer is optional - always can proceed
        return true;
      case 4:
        // Options step - always can proceed
        return true;
      case 5:
        return (
          formData.client_phone &&
          formData.client_address
        );
      default:
        return true;
    }
  };

  const steps = [
    { num: 1, label: "Date" },
    { num: 2, label: "Structure" },
    { num: 3, label: "Offre" },
    { num: 4, label: "Options" },
    { num: 5, label: "Vos infos" },
  ];

  const availableStructuresCount = structures.filter(s => isStructureAvailable(s.id)).length;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-cyan-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Page non trouvée</h1>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-cyan-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Demande envoyée !</h1>
            <p className="text-gray-600 mb-6">{success.message}</p>
            
            <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2 mb-6">
              <div className="flex justify-between">
                <span className="text-gray-500">Statut</span>
                <Badge className="bg-amber-100 text-amber-800">EN ATTENTE</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total estimé</span>
                <span className="font-bold text-pink-600">{success.total_price}€</span>
              </div>
            </div>

            {(success.organization_phone || success.organization_email) && (
              <div className="text-sm text-gray-500">
                <p className="mb-2">Pour toute question :</p>
                {success.organization_phone && (
                  <p className="flex items-center justify-center gap-2">
                    <Phone className="w-4 h-4" />
                    <a href={`tel:${success.organization_phone}`} className="text-pink-600 hover:underline">
                      {success.organization_phone}
                    </a>
                  </p>
                )}
                {success.organization_email && (
                  <p className="flex items-center justify-center gap-2 mt-1">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${success.organization_email}`} className="text-pink-600 hover:underline">
                      {success.organization_email}
                    </a>
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-pink-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-lg">
            <Castle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{organization?.name}</h1>
            <p className="text-sm text-gray-500">Réservation en ligne</p>
          </div>
        </div>
      </header>

      <main className="p-4 max-w-2xl mx-auto pb-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6 mt-4">
          {steps.map((s, i) => (
            <div key={s.num} className="flex items-center">
              <div
                className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-colors ${
                  step >= s.num
                    ? "bg-gradient-to-br from-pink-500 to-purple-600 text-white shadow-lg"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                {step > s.num ? <Check className="w-5 h-5" /> : s.num}
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`w-4 md:w-10 h-1 mx-1 rounded ${
                    step > s.num ? "bg-pink-500" : "bg-gray-200"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Date & Time */}
        {step === 1 && (
          <div className="space-y-4 animate-fade-in">
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-pink-500" />
                  Choisissez une date
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={formData.date}
                  onSelect={(date) => updateField("date", date)}
                  locale={fr}
                  disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                  className="rounded-xl border-0"
                />
              </CardContent>
            </Card>

            {formData.date && (
              <Card className="shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-500" />
                    Créneau horaire
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <RadioGroup
                    value={formData.time_slot}
                    onValueChange={(v) => updateField("time_slot", v)}
                    className="grid gap-3"
                  >
                    {timeSlots.map((slot) => (
                      <Label
                        key={slot.id}
                        htmlFor={`slot-${slot.id}`}
                        className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                          formData.time_slot === slot.id
                            ? "border-purple-500 bg-purple-50 shadow-md"
                            : "border-gray-100 hover:border-purple-200 hover:bg-purple-50/50"
                        }`}
                      >
                        <RadioGroupItem value={slot.id} id={`slot-${slot.id}`} />
                        <div className="flex-1">
                          <p className="font-semibold">{slot.name}</p>
                          <p className="text-sm text-gray-500">{slot.hours}</p>
                        </div>
                      </Label>
                    ))}
                  </RadioGroup>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Step 2: Structure Selection */}
        {step === 2 && (
          <div className="space-y-4 animate-fade-in">
            <Card className="shadow-lg border-0 bg-gradient-to-r from-pink-50 to-purple-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-pink-500" />
                    <span className="font-medium">
                      {format(formData.date, "EEEE d MMMM yyyy", { locale: fr })}
                    </span>
                  </div>
                  <Badge variant="outline" className="bg-white">
                    {timeSlots.find(t => t.id === formData.time_slot)?.name}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Castle className="w-5 h-5 text-pink-500" />
                  Choisir la structure
                  {loadingAvailability && (
                    <div className="w-4 h-4 border-2 border-pink-500 border-t-transparent rounded-full animate-spin ml-2" />
                  )}
                </CardTitle>
                <p className="text-sm text-gray-500">
                  {availableStructuresCount} structure{availableStructuresCount > 1 ? "s" : ""} disponible{availableStructuresCount > 1 ? "s" : ""}
                </p>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={formData.structure_id}
                  onValueChange={(v) => updateField("structure_id", v)}
                  className="grid gap-3"
                >
                  {structures.map((structure) => {
                    const available = isStructureAvailable(structure.id);
                    return (
                      <Label
                        key={structure.id}
                        htmlFor={structure.id}
                        className={`flex items-center gap-4 p-3 rounded-2xl border-2 transition-all ${
                          !available
                            ? "border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed"
                            : formData.structure_id === structure.id
                            ? "border-pink-500 bg-pink-50 cursor-pointer shadow-md"
                            : "border-gray-100 hover:border-pink-200 hover:bg-pink-50/50 cursor-pointer"
                        }`}
                      >
                        <RadioGroupItem
                          value={structure.id}
                          id={structure.id}
                          disabled={!available}
                        />
                        {/* Structure Image with zoom capability */}
                        <div 
                          className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100 relative group cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (structure.image) {
                              setLightboxImage({ url: getImageUrl(structure.image), name: structure.name });
                            }
                          }}
                        >
                          {structure.image ? (
                            <>
                              <img 
                                src={getImageUrl(structure.image)} 
                                alt={structure.name}
                                className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Castle className="w-8 h-8 text-gray-300" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold truncate">{structure.name}</p>
                            {available ? (
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 flex-shrink-0">
                                Indisponible
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{structure.dimensions}</p>
                          {/* Display price based on time slot */}
                          {(structure.price_half_day > 0 || structure.price_full_day > 0) && (
                            <p className="text-sm font-medium text-pink-600 mt-1">
                              {formData.time_slot === "journee" 
                                ? `${structure.price_full_day}€ / journée`
                                : `${structure.price_half_day}€ / demi-journée`
                              }
                              <span className="text-gray-400 font-normal ml-1">(location seule)</span>
                            </p>
                          )}
                        </div>
                      </Label>
                    );
                  })}
                </RadioGroup>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Offer Selection */}
        {step === 3 && (
          <div className="space-y-4 animate-fade-in">
            <Card className="shadow-lg border-0 bg-gradient-to-r from-pink-50 to-purple-50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 text-pink-500" />
                    <span className="text-sm">
                      {format(formData.date, "d MMM yyyy", { locale: fr })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Castle className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium">
                      {structures.find(s => s.id === formData.structure_id)?.name}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PartyPopper className="w-5 h-5 text-purple-500" />
                  Choisir votre formule
                </CardTitle>
                <p className="text-sm text-gray-500">Optionnel - Vous pouvez aussi louer la structure seule</p>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={formData.offer_id}
                  onValueChange={(v) => updateField("offer_id", v)}
                  className="grid gap-3"
                >
                  {/* Option: No offer - structure only */}
                  <Label
                    htmlFor="offer-none"
                    className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                      formData.offer_id === ""
                        ? "border-green-500 bg-green-50 shadow-md"
                        : "border-gray-100 hover:border-green-200 hover:bg-green-50/50"
                    }`}
                  >
                    <RadioGroupItem value="" id="offer-none" />
                    <div className="flex-1">
                      <p className="font-semibold">Location seule (sans formule)</p>
                      <p className="text-sm text-gray-500">Louer uniquement la structure sélectionnée</p>
                    </div>
                    <span className="font-bold text-green-600 text-lg">
                      {getStructurePrice(structures.find(s => s.id === formData.structure_id))}€
                    </span>
                  </Label>

                  {/* Compatible offers */}
                  {getCompatibleOffers().map((offer) => {
                    const supplement = offer.included_structures?.find(inc => inc.structure_id === formData.structure_id)?.supplement || 0;
                    return (
                      <Label
                        key={offer.id}
                        htmlFor={`offer-${offer.id}`}
                        className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                          formData.offer_id === offer.id
                            ? "border-purple-500 bg-purple-50 shadow-md"
                            : "border-gray-100 hover:border-purple-200 hover:bg-purple-50/50"
                        }`}
                      >
                        <RadioGroupItem value={offer.id} id={`offer-${offer.id}`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold">{offer.name}</p>
                            {offer.requires_validation && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                                Sur devis
                              </span>
                            )}
                            {supplement > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                                +{supplement}€ supplément
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{offer.description}</p>
                        </div>
                        <div className="text-right">
                          <span className="font-bold text-pink-600 text-lg">{offer.price + supplement}€</span>
                          {supplement > 0 && (
                            <p className="text-xs text-gray-400">({offer.price}€ + {supplement}€)</p>
                          )}
                        </div>
                      </Label>
                    );
                  })}

                  {getCompatibleOffers().length === 0 && (
                    <p className="text-center text-gray-500 py-4 bg-gray-50 rounded-xl">
                      Aucune formule disponible pour cette structure
                    </p>
                  )}
                </RadioGroup>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 5: Client Info */}
        {step === 5 && (
          <Card className="shadow-lg border-0 animate-fade-in">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-pink-500" />
                Vos coordonnées
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="client_name">Nom complet</Label>
                <Input
                  id="client_name"
                  value={formData.client_name}
                  onChange={(e) => updateField("client_name", e.target.value)}
                  className="mt-1 h-12 rounded-xl border-gray-200 focus:border-pink-500 focus:ring-pink-500"
                  placeholder="Jean Dupont"
                />
              </div>

              <div>
                <Label htmlFor="client_phone">Téléphone *</Label>
                <Input
                  id="client_phone"
                  type="tel"
                  value={formData.client_phone}
                  onChange={(e) => updateField("client_phone", e.target.value)}
                  className="mt-1 h-12 rounded-xl border-gray-200 focus:border-pink-500 focus:ring-pink-500"
                  placeholder="0692 XX XX XX"
                />
              </div>

              <div>
                <Label htmlFor="client_email">Email</Label>
                <Input
                  id="client_email"
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => updateField("client_email", e.target.value)}
                  className="mt-1 h-12 rounded-xl border-gray-200 focus:border-pink-500 focus:ring-pink-500"
                  placeholder="jean@example.com"
                />
              </div>

              <div>
                <Label htmlFor="client_address">Adresse de livraison *</Label>
                <Textarea
                  id="client_address"
                  value={formData.client_address}
                  onChange={(e) => updateField("client_address", e.target.value)}
                  className="mt-1 min-h-[80px] rounded-xl border-gray-200 focus:border-pink-500 focus:ring-pink-500"
                  placeholder="123 Rue Example, 97400 Saint-Denis"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Options */}
        {step === 4 && (
          <div className="space-y-4 animate-fade-in">
            {options.length > 0 && (
              <Card className="shadow-lg border-0">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    Options complémentaires
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {options.map((option) => (
                    <Label
                      key={option.id}
                      htmlFor={`option-${option.id}`}
                      className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                        formData.options.includes(option.id)
                          ? "border-amber-400 bg-amber-50 shadow-md"
                          : "border-gray-100 hover:border-amber-200 hover:bg-amber-50/50"
                      }`}
                    >
                      <Checkbox
                        id={`option-${option.id}`}
                        checked={formData.options.includes(option.id)}
                        onCheckedChange={() => toggleOption(option.id)}
                      />
                      <div className="flex-1">
                        <p className="font-semibold">{option.name}</p>
                        {option.description && (
                          <p className="text-sm text-gray-500">{option.description}</p>
                        )}
                      </div>
                      <span className="font-bold text-pink-600">+{option.price}€</span>
                    </Label>
                  ))}
                </CardContent>
              </Card>
            )}

            <Card className="shadow-lg border-0">
              <CardContent className="p-4">
                <Label
                  htmlFor="needs_generator"
                  className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                    formData.needs_generator
                      ? "border-cyan-400 bg-cyan-50 shadow-md"
                      : "border-gray-100 hover:border-cyan-200 hover:bg-cyan-50/50"
                  }`}
                >
                  <Checkbox
                    id="needs_generator"
                    checked={formData.needs_generator}
                    onCheckedChange={(checked) => updateField("needs_generator", checked)}
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Zap className="w-5 h-5 text-cyan-500" />
                    <div>
                      <p className="font-semibold">Groupe électrogène</p>
                      <p className="text-sm text-gray-500">Si pas d&apos;accès électrique</p>
                    </div>
                  </div>
                  <span className="font-bold text-pink-600">+50€</span>
                </Label>
              </CardContent>
            </Card>

            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle>Notes ou demandes particulières</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => updateField("notes", e.target.value)}
                  className="min-h-[100px] rounded-xl border-gray-200 focus:border-pink-500 focus:ring-pink-500"
                  placeholder="Informations supplémentaires..."
                />
              </CardContent>
            </Card>

            {/* Summary */}
            <Card className="shadow-lg border-0 bg-gradient-to-br from-pink-50 to-purple-50">
              <CardContent className="p-6">
                <h3 className="font-bold text-lg mb-4">Récapitulatif</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date</span>
                    <span className="font-medium">
                      {formData.date && format(formData.date, "d MMMM yyyy", { locale: fr })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Créneau</span>
                    <span className="font-medium">
                      {timeSlots.find((t) => t.id === formData.time_slot)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Structure</span>
                    <span className="font-medium">
                      {structures.find((s) => s.id === formData.structure_id)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Formule</span>
                    <span className="font-medium">
                      {offers.find((o) => o.id === formData.offer_id)?.name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Client</span>
                    <span className="font-medium">{formData.client_name}</span>
                  </div>
                  {formData.options.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Options</span>
                      <span className="font-medium text-right">
                        {formData.options
                          .map((id) => options.find((o) => o.id === id)?.name)
                          .join(", ")}
                      </span>
                    </div>
                  )}
                  {formData.needs_generator && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Groupe électrogène</span>
                      <span className="font-medium">Oui (+50€)</span>
                    </div>
                  )}
                  <div className="border-t border-pink-200 pt-3 mt-3 flex justify-between text-lg font-bold">
                    <span>Total estimé</span>
                    <span className="text-pink-600">{calculateTotal()}€</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-3 mt-6">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1 h-12 rounded-xl border-2 border-pink-200 hover:bg-pink-50"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Retour
            </Button>
          )}

          {step < 5 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="flex-1 h-12 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl shadow-lg"
            >
              Suivant
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formData.client_phone || !formData.client_address}
              className="flex-1 h-12 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Check className="w-5 h-5 mr-1" />
                  Envoyer ma demande
                </>
              )}
            </Button>
          )}
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Votre demande sera confirmée par notre équipe dans les plus brefs délais.
        </p>
      </main>

      {/* Image Lightbox Modal */}
      <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
        <DialogContent className="max-w-3xl p-0 bg-transparent border-none shadow-none">
          <div className="relative">
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            {lightboxImage && (
              <div className="bg-white rounded-xl overflow-hidden shadow-2xl">
                <img
                  src={lightboxImage.url}
                  alt={lightboxImage.name}
                  className="w-full h-auto max-h-[70vh] object-contain"
                />
                <div className="p-4 bg-white">
                  <h3 className="text-lg font-semibold text-center">{lightboxImage.name}</h3>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Toaster position="top-center" richColors />
    </div>
  );
}
